// React Query
import { useMutation, useQuery } from "@tanstack/react-query";

// MUI
import { Box, Button, Stack } from "@mui/material";

// API
import { getGpsStores, updateGpsStore } from "@Settings/api/AdministrationTools";

// Helpers
import { or } from "@Common/helpers/sonar";
import { hasSomeRoleLoggedIn } from "@Settings/helpers/AdministrationTools/Provider/utils";

// Components
import SmartSelector from "@Layouts/components/InputComponents/SmartSelector";
import InfoSubTitle from "@Settings/components/SystemSettings/common/InfoSubTitle";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Types
import { TSelectValue } from "@Layouts/components/InputComponents/SmartSelector/interfaces";
import { GPSSelectedProvider, TGpsStore } from "@Settings/types/administrationTools/utils";

// Constants
import { PROVIDERS_WITH_STORE_FEATURE } from "@/Settings/Constants/AdministrationTools/AdministrationTools";

type Props = Readonly<{
	locationId: string;
	selectedProvider: GPSSelectedProvider | null;
}>;

export default function GPSStore(props: Props) {
	const { provider, providerDBProperty = "", gpsStoreName = "", gpsStoreId = "" } = props.selectedProvider ?? {};

	/********************************************* QUERIES ************************************************/
	const {
		data: stores,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: ["gpsStores", provider],
		queryFn: async () => getGpsStores(props.locationId, providerDBProperty),
		enabled: false,
	});

	/********************************************* MUTATIONS ************************************************/
	const { mutate: mutateGpsStore, isPending } = useMutation({
		mutationKey: ["updateGpsStore", provider],
		mutationFn: async (gpsStoreInput: TGpsStore) => updateGpsStore(props.locationId, providerDBProperty, gpsStoreInput),
	});

	/********************************************* HANDLERS ************************************************/
	const handleUpdateStore = (itemSelected: TSelectValue) => {
		const gpsStoreInput = itemSelected as { gpsStoreName: string; gpsStoreId: string; provider: string };
		mutateGpsStore(gpsStoreInput);
	};

	const isDisabled = or(
		isLoading,
		!provider,
		!PROVIDERS_WITH_STORE_FEATURE.includes(providerDBProperty),
		!hasSomeRoleLoggedIn(props.selectedProvider)
	);

	return (
		<Stack>
			<InfoSubTitle title={"Store Select"} className={"!mb-[12px] !mt-8"} />
			<Stack direction={"row"} spacing={3}>
				<Box width={200}>
					<SmartSelector
						options={(stores ?? []) as object[]}
						selectBy={"gpsStoreName"}
						showBy={"gpsStoreName"}
						selected={{
							gpsStoreName: gpsStoreName ?? "",
						}}
						loading={isLoading || isPending}
						disabled={isDisabled || isPending}
						inputProps={{
							placeholder: "Store Select",
						}}
						listBoxProps={{
							sx: { maxHeight: "220px" },
						}}
						// Callbacks
						onChangeAction={(value) => handleUpdateStore({ ...value, provider })}
					/>
				</Box>
				<Button
					onClick={() => refetch()}
					disabled={isDisabled || isLoading || isPending}
					classes={{
						root: `!bg-[#2357BE] !font-sans !font-normal !text-sm !rounded-[6px] !capitalize !h-[30px] !shadow-none !text-white !w-[82px] ${
							isDisabled ? "!bg-[#585B6D]" : ""
						}`,
						disabled: "!bg-[#CCCCCC] !text-white",
					}}
				>
					Load Stores
				</Button>
			</Stack>

			<TypographyCell fontSize={"12px"} fontWeight={"400"}>
				Store ID: {gpsStoreId}
			</TypographyCell>
		</Stack>
	);
}
